/* Feb-2026 · Permissions Panel
 *
 * Dedicated surface for assigning admin scopes to admin users.
 * Extracted from AdminUsersPanel for cleaner information architecture:
 * `Admin Users` now handles CRUD (create/delete/reset password) while
 * this panel focuses on the permission matrix.
 *
 * Backend: reuses the existing adminApi endpoints (adminUsersList,
 * adminUsersSetScopes, adminUsersScopeSeats).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { PanelHeader } from './_shared';
import {
  Shield, Store, GraduationCap, PenTool, BookText, FileText, ShieldCheck,
  Landmark, Eye, KeyRound, RotateCw, Search, Lock, Film,
} from 'lucide-react';

const SCOPE_META = {
  pro_writer:    { icon: Shield,        label: 'Admin (pro_writer)',   help: 'Full Admin console — Admin Overview + everything.' },
  bookstore:     { icon: Store,         label: 'Bookstore',            help: 'Bookstore admin surface.' },
  coach:         { icon: GraduationCap, label: 'Coach',                help: 'Premier Studio coaching queue.' },
  editor:        { icon: PenTool,       label: 'Editor',               help: 'Premier Studio editor-review queue.' },
  financials:    { icon: Landmark,      label: 'Financials (CFO)',     help: 'All Finance panels + ledger + AP/AR.' },
  it_management: { icon: ShieldCheck,   label: 'IT Management',        help: 'CTO panel — network, secrets, cron.' },
  viewer:        { icon: Eye,           label: 'Viewer (CEO)',         help: 'Read-only global view.' },
  ledger_entry:  { icon: BookText,      label: 'Ledger clerk',         help: 'Post journal entries only.' },
  ap_ar:         { icon: FileText,      label: 'AR / AP clerk',        help: 'Prepare AP/AR (AP routes to approval).' },
  ap_approver:   { icon: ShieldCheck,   label: 'AP approver',          help: 'CFO-tier disbursement approver.' },
  compliance:    { icon: ShieldCheck,   label: 'Compliance',           help: 'Compliance & Licenses upload + viewer.' },
  marketing_media: { icon: Film,        label: 'Marketing & Media',     help: 'Training media library and production queue.' },
};

const CATEGORIES = [
  { title: 'Admin & Finance',    scopes: ['pro_writer', 'financials', 'viewer'] },
  { title: 'Ledger & AP',        scopes: ['ledger_entry', 'ap_ar', 'ap_approver'] },
  { title: 'Compliance',         scopes: ['compliance'] },
  { title: 'IT',                 scopes: ['it_management'] },
  { title: 'Editorial',          scopes: ['coach', 'editor'] },
  { title: 'Marketing & Media',  scopes: ['marketing_media'] },
  { title: 'Bookstore',          scopes: ['bookstore'] },
];

export default function PermissionsPanel() {
  const [rows, setRows]       = useState([]);
  const [valid, setValid]     = useState([]);
  const [seeded, setSeeded]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter]   = useState('');
  const [seats, setSeats]     = useState({});

  const reload = async () => {
    setLoading(true);
    try {
      const r = await adminApi.adminUsersList();
      setRows(r.users || []);
      setValid(r.valid_scopes || []);
      setSeeded(r.seeded_usernames || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load admin users.');
    } finally {
      setLoading(false);
    }
  };

  const reloadSeats = async () => {
    try {
      if (typeof adminApi.adminUsersScopeSeats === 'function') {
        const r = await adminApi.adminUsersScopeSeats();
        setSeats(r.seats || {});
      }
    } catch { /* seat info is nice-to-have */ }
  };

  useEffect(() => { reload(); reloadSeats(); }, []);

  const toggleScope = async (row, scope) => {
    const current = new Set(row.scopes || []);
    if (current.has(scope)) current.delete(scope); else current.add(scope);
    try {
      await adminApi.adminUsersSetScopes(row.username, Array.from(current));
      toast.success(`Updated ${row.username}: ${scope} ${current.has(scope) ? 'granted' : 'revoked'}.`);
      reload();
      reloadSeats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed.');
    }
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.username.toLowerCase().includes(q));
  }, [rows, filter]);

  return (
    <div className="space-y-4" data-testid="permissions-panel">
      <PanelHeader
        icon={KeyRound}
        title="Permissions"
        subtitle="Grant or revoke admin scopes per user. Every scope maps to a purposeful subset of the admin console — the sidebar re-arranges itself to match the assigned scopes on next login."
        onRefresh={reload}
      />

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="permissions-filter"
            className="h-8 pl-7 text-[12.5px]"
            placeholder="Filter by username…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <Button size="sm" variant="ghost" onClick={() => { reload(); reloadSeats(); }} disabled={loading} data-testid="permissions-refresh">
          <RotateCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Seat map — cap counts per scope */}
      {Object.keys(seats).length > 0 && (
        <div className="rounded-md border border-border bg-card p-3" data-testid="permissions-seat-map">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Seat caps
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
            {Object.entries(seats).map(([scope, info]) => {
              const meta = SCOPE_META[scope] || { label: scope };
              const remaining = info.remaining ?? (info.limit - info.used);
              const tone = remaining <= 0 ? 'text-rose-700' : remaining <= 1 ? 'text-amber-700' : 'text-muted-foreground';
              return (
                <div key={scope} className="rounded-md border border-border bg-panel-header/30 p-2 text-[11.5px]">
                  <div className="font-semibold">{meta.label}</div>
                  <div className={tone}>
                    {info.used}/{info.limit} used · {remaining} remaining
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Matrix — grouped by category */}
      {CATEGORIES.map((cat) => {
        const catScopes = cat.scopes.filter((s) => valid.includes(s));
        if (catScopes.length === 0) return null;
        return (
          <div key={cat.title} className="rounded-md border border-border bg-card shadow-panel" data-testid={`permissions-cat-${cat.title.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="border-b border-border bg-panel-header/40 px-3 py-2 text-[12px] font-semibold">
              {cat.title}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-panel-header/20 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">User</th>
                    {catScopes.map((scope) => {
                      const meta = SCOPE_META[scope] || { label: scope, icon: KeyRound };
                      const Icon = meta.icon;
                      return (
                        <th key={scope} className="px-3 py-2 text-center" title={meta.help}>
                          <span className="inline-flex items-center gap-1">
                            <Icon className="h-3 w-3" /> {meta.label}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => {
                    const isSeeded = seeded.includes(row.username);
                    return (
                      <tr key={`${cat.title}-${row.username}`} className="border-t border-border hover:bg-secondary/40" data-testid={`permissions-row-${cat.title.replace(/\s+/g, '-').toLowerCase()}-${row.username}`}>
                        <td className="px-3 py-2">
                          <div className="font-mono-stat">{row.username}</div>
                          {isSeeded && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                              <Lock className="h-2.5 w-2.5" /> seeded
                            </span>
                          )}
                        </td>
                        {catScopes.map((scope) => (
                          <td key={scope} className="px-3 py-2 text-center">
                            <Switch
                              checked={(row.scopes || []).includes(scope)}
                              onCheckedChange={() => toggleScope(row, scope)}
                              data-testid={`permissions-toggle-${row.username}-${scope}`}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={catScopes.length + 1} className="px-3 py-6 text-center text-muted-foreground">No users match filter.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <div className="rounded-md border border-dashed border-border bg-panel-header/20 p-3 text-[11px] text-muted-foreground">
        Need to create or delete an admin account? Go to <b>Admin Users</b>. Need to review activity? Every scope change is written to the audit trail there.
      </div>
    </div>
  );
}
